import { useCallback, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { FlowPromptItem, UpsertFlowPromptRequest } from "@/api/models"

import { BookOpen, Pencil, Plus, Save, Trash2 } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"

import { useDeleteFlowPrompt } from "../hooks/use-delete-flow-prompt"
import { useGetFlowPrompts } from "../hooks/use-get-flow-prompts"
import { useUpsertFlowPrompt } from "../hooks/use-upsert-flow-prompt"

type FlowPromptSelectorProps = {
  onPromptSelect: (content: string) => void
  className?: string
}

export function FlowPromptSelector({ onPromptSelect, className }: FlowPromptSelectorProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "workflowLanding.flowPrompts" })
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingPrompt, setEditingPrompt] = useState<FlowPromptItem | null>(null)

  const settingsService = SettingsService.getInstance()
  const isWorkflowAdmin = settingsService.settings.user_policy.is_workflow_admin

  const { data: promptsData, isPending, isError } = useGetFlowPrompts()

  const prompts: FlowPromptItem[] = promptsData?.prompts ?? []
  const personalPrompts = prompts.filter((p) => p.type === "flow_personal")
  const companyPrompts = prompts.filter((p) => p.type === "flow_company")

  const handlePromptClick = useCallback(
    (prompt: FlowPromptItem) => {
      onPromptSelect(prompt.content)
    },
    [onPromptSelect]
  )

  const handleManageClick = useCallback(() => {
    setEditingPrompt(null)
    setDialogOpen(true)
  }, [])

  const handleEditPrompt = useCallback((prompt: FlowPromptItem) => {
    setEditingPrompt(prompt)
    setDialogOpen(true)
  }, [])

  return (
    <>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                aria-label={t("title")}
                className={cn("nodrag size-6 p-0", className)}
                size="icon"
                variant="ghost"
              >
                <BookOpen className="size-3.5" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p>{t("title")}</p>
          </TooltipContent>
        </Tooltip>

        <DropdownMenuContent align="end" className="w-56">
          {isPending && <DropdownMenuItem disabled>{t("loading")}</DropdownMenuItem>}
          {isError && (
            <DropdownMenuItem disabled className="text-destructive">
              {t("loadFailed")}
            </DropdownMenuItem>
          )}
          {!isPending && !isError && (
            <>
              {companyPrompts.length > 0 && (
                <DropdownMenuGroup>
                  <DropdownMenuLabel>{t("company")}</DropdownMenuLabel>
                  {companyPrompts.map((prompt) => (
                    <DropdownMenuItem
                      key={prompt.id}
                      className="group flex cursor-pointer items-center justify-between"
                      onSelect={() => handlePromptClick(prompt)}
                    >
                      <span className="flex-1 truncate">{prompt.title}</span>
                      {isWorkflowAdmin && (
                        <Button
                          className="hover:bg-muted-foreground/20 -m-0.5 size-6 p-0 opacity-0 transition-opacity group-hover:opacity-100"
                          size="icon"
                          variant="ghost"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            handleEditPrompt(prompt)
                          }}
                        >
                          <Pencil className="size-3" />
                        </Button>
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              )}

              {personalPrompts.length > 0 && (
                <>
                  {companyPrompts.length > 0 && <DropdownMenuSeparator />}
                  <DropdownMenuGroup>
                    <DropdownMenuLabel>{t("personal")}</DropdownMenuLabel>
                    {personalPrompts.map((prompt) => (
                      <DropdownMenuItem
                        key={prompt.id}
                        className="group flex cursor-pointer items-center justify-between"
                        onSelect={() => handlePromptClick(prompt)}
                      >
                        <span className="flex-1 truncate">{prompt.title}</span>
                        <Button
                          className="hover:bg-muted-foreground/20 -m-0.5 size-6 p-0 opacity-0 transition-opacity group-hover:opacity-100"
                          size="icon"
                          variant="ghost"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            handleEditPrompt(prompt)
                          }}
                        >
                          <Pencil className="size-3" />
                        </Button>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuGroup>
                </>
              )}

              {prompts.length === 0 && (
                <DropdownMenuItem disabled>{t("noPrompts")}</DropdownMenuItem>
              )}
            </>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleManageClick}>
            <Plus className="mr-2 size-4" />
            {t("createNew")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <FlowPromptDialog
        editingPrompt={editingPrompt}
        isWorkflowAdmin={isWorkflowAdmin}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </>
  )
}

type FlowPromptDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  editingPrompt: FlowPromptItem | null
  isWorkflowAdmin: boolean
}

function FlowPromptDialog({
  open,
  onOpenChange,
  editingPrompt,
  isWorkflowAdmin,
}: FlowPromptDialogProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "workflowLanding.flowPrompts" })

  const [title, setTitle] = useState("")
  const [content, setContent] = useState("")
  const [isCompanyPrompt, setIsCompanyPrompt] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const upsertMutation = useUpsertFlowPrompt()
  const deleteMutation = useDeleteFlowPrompt()

  const isEditing = editingPrompt !== null
  // Admins can toggle between company and personal in both directions (consistent with workflow visibility toggle)
  const canEditAsCompany = isWorkflowAdmin

  // Reset form when dialog opens or editingPrompt changes
  useEffect(() => {
    if (open) {
      if (editingPrompt) {
        setTitle(editingPrompt.title)
        setContent(editingPrompt.content)
        setIsCompanyPrompt(editingPrompt.type === "flow_company")
      } else {
        setTitle("")
        setContent("")
        setIsCompanyPrompt(false)
      }
    }
  }, [open, editingPrompt])

  const handleSave = useCallback(async () => {
    if (!title.trim() || !content.trim()) {
      toast.error(t("titleAndContentRequired"))
      return
    }

    const promptData: UpsertFlowPromptRequest = {
      id: editingPrompt?.id,
      title: title.trim(),
      content: content.trim(),
      type: isCompanyPrompt ? "flow_company" : "flow_personal",
    }

    try {
      await upsertMutation.mutateAsync(promptData)
      toast.success(isEditing ? t("updated") : t("created"))
      onOpenChange(false)
    } catch {
      toast.error(t("saveFailed"))
    }
  }, [title, content, isCompanyPrompt, editingPrompt, isEditing, upsertMutation, onOpenChange, t])

  const handleDeleteClick = useCallback(() => {
    setShowDeleteConfirm(true)
  }, [])

  const handleConfirmDelete = useCallback(async () => {
    if (!editingPrompt) return

    try {
      await deleteMutation.mutateAsync({
        promptId: editingPrompt.id,
        type: editingPrompt.type,
      })
      toast.success(t("deleted"))
      setShowDeleteConfirm(false)
      onOpenChange(false)
    } catch {
      toast.error(t("deleteFailed"))
    }
  }, [editingPrompt, deleteMutation, onOpenChange, t])

  const isMutating = upsertMutation.isPending || deleteMutation.isPending
  const canDelete = isEditing && (editingPrompt.type === "flow_personal" || isWorkflowAdmin)

  let saveButtonText = isEditing ? t("save") : t("create")
  if (upsertMutation.isPending) {
    saveButtonText = isEditing ? t("saving") : t("creating")
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? t("editTitle") : t("createTitle")}</DialogTitle>
          <DialogDescription>{t("dialogDescription")}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="prompt-title">{t("nameLabel")}</Label>
            <Input
              disabled={isMutating}
              id="prompt-title"
              placeholder={t("namePlaceholder")}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="prompt-content">{t("contentLabel")}</Label>
            <Textarea
              className="min-h-[150px]"
              disabled={isMutating}
              id="prompt-content"
              placeholder={t("contentPlaceholder")}
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
          </div>

          {canEditAsCompany && (
            <div className="flex items-center justify-between rounded-md border p-3">
              <div className="space-y-0.5">
                <Label className="text-sm font-medium">{t("companyPromptLabel")}</Label>
                <div className="text-muted-foreground text-xs">{t("companyPromptDescription")}</div>
              </div>
              <Switch
                checked={isCompanyPrompt}
                disabled={isMutating}
                onCheckedChange={setIsCompanyPrompt}
              />
            </div>
          )}
        </div>

        <DialogFooter className="flex-row justify-between sm:justify-between">
          <div>
            {canDelete && (
              <Button
                disabled={isMutating}
                type="button"
                variant="destructive"
                onClick={handleDeleteClick}
              >
                <Trash2 className="mr-2 size-4" />
                {deleteMutation.isPending ? t("deleting") : t("delete")}
              </Button>
            )}
          </div>
          <Button
            disabled={isMutating || !title.trim() || !content.trim()}
            type="button"
            onClick={handleSave}
          >
            <Save className="mr-2 size-4" />
            {saveButtonText}
          </Button>
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirmDeleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("confirmDelete")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction disabled={deleteMutation.isPending} onClick={handleConfirmDelete}>
              {deleteMutation.isPending ? t("deleting") : t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  )
}
